// Icing — touch-icing variant.
//
// Owns:   the rule that whistles play dead when a team shoots the
//         puck from their own half of centre ice across the opposing
//         goal line, untouched along the way.
// Reads:  state.events (this tick's ShotReleased), state.puck (pos,
//         vel, lastTouchedBy), state.rules.icing, tuning.rink.
// Mutates: state.rules.icing (armed / cleared), state.puck +
//         state.clock (via `enterStoppage` when the whistle fires).
// Emits:  `Stoppage{ reason: "Icing" }`.
//
// Implementation:
//   1. Resolve any armed icing first:
//      - If the puck's `lastTouchedBy` is someone other than the
//        original shooter, the puck has been touched — clear the arm.
//      - Otherwise, if the puck just crossed the opposing goal line
//        this tick, whistle for icing. Faceoff in the offending
//        team's defensive zone, on the side the puck was shot from.
//   2. Arm a fresh icing from this tick's ShotReleased event(s) if
//      the shooter was in their own half of centre ice (x < 0 for
//      HOME, x > 0 for AWAY).
//
// Why touch-icing (vs. no-touch / hybrid):
//   - No-touch: whistle the moment the puck crosses the goal line.
//     Feels harsh for arcade — many "almost icing" plays die early.
//   - Hybrid: linesman judges whether the defender would have reached
//     the puck first. Too judgement-call-y to encode cleanly.
//   - Touch: the defender must actually touch the puck to wave it off.
//     Best fit for arcade — preserves the dump-and-recover beat.
//
// OT exception: during 3-on-3 overtime, icing is disabled to keep play
// flowing (Slice 8 toggles this). For now the rule fires in all phases.
//
// Called from `core/SimTick.ts` Step 16b — after offside (16a), before
// goal detection (17). Same justification: icing firing in the same
// tick as a would-be goal must cancel the goal.
//
// Zero-allocation.

import type { MatchEvent } from "@arcade-hockey/shared";
import { defensiveZoneSpots, nearestFaceoffSpot } from "../state/faceoff-spots.js";
import { tuning } from "../state/tuning.js";
import type { MatchState } from "../state/types.js";
import { enterStoppage } from "./stoppage.js";

export function tickIcing(state: MatchState, prevPuckX: number): void {
  // Icing only enforces during live Play.
  if (state.clock.phase !== "Play") {
    state.rules.icing = undefined;
    return;
  }

  const halfL = tuning.rink.length / 2;
  const goalLineOffset = tuning.rink.goalLineOffset;
  const homeGoalLineX = -halfL + goalLineOffset; // -26.4
  const awayGoalLineX = halfL - goalLineOffset; // +26.4

  // Step 1 — Resolve any armed icing.
  if (state.rules.icing !== undefined) {
    const arm = state.rules.icing;

    // Cancellation: the puck has been touched by someone other than the
    // shooter. `lastTouchedBy` is updated by every system that touches
    // the puck (possession.ts pickup, puck-strip, body-check, goalie
    // saves, controlled→pass/shoot transition), so this single
    // comparison covers all cancellation paths.
    if (state.puck.lastTouchedBy !== null && state.puck.lastTouchedBy !== arm.shooterId) {
      state.rules.icing = undefined;
    } else {
      // Did the puck just cross the opposing goal line this tick?
      // HOME's icing target is the away goal line at +26.4; AWAY's is
      // the home goal line at -26.4.
      const targetLineX = arm.team === "home" ? awayGoalLineX : homeGoalLineX;
      const dir = arm.team === "home" ? 1 : -1;
      const crossed =
        dir > 0
          ? prevPuckX < targetLineX && state.puck.pos.x >= targetLineX
          : prevPuckX > targetLineX && state.puck.pos.x <= targetLineX;
      if (crossed) {
        // Whistle: faceoff in the icing team's DZ on the side the
        // puck was originally shot from. The side is picked by the
        // shot's y at arm time — left side (+y) → Home/AwayZoneLeft.
        const dot = nearestFaceoffSpot(
          arm.team === "home" ? -halfL : halfL,
          arm.shotFromY,
          defensiveZoneSpots(arm.team),
        );
        enterStoppage(state, dot, "Icing");
        state.rules.icing = undefined;
        return;
      }
    }
  }

  // Step 2 — Arm a fresh icing from this tick's ShotReleased event.
  // Walk events backward from the end while ev.tick === state.tick.
  // At most a handful of events per tick, so this is cheap.
  for (let i = state.events.length - 1; i >= 0; i--) {
    const ev = state.events[i] as MatchEvent | undefined;
    if (ev === undefined) break;
    if (ev.tick !== state.tick) break; // older tick — no more this-tick events
    if (ev.type !== "ShotReleased") continue;
    const shooter = state.playersById.get(ev.from);
    if (shooter === undefined) continue;
    // Arm only if the shooter was in their own half of centre ice
    // (x < 0 for HOME, x > 0 for AWAY).
    const inOwnHalf = shooter.teamId === "home" ? shooter.pos.x < 0 : shooter.pos.x > 0;
    if (!inOwnHalf) continue;
    state.rules.icing = {
      team: shooter.teamId,
      shooterId: shooter.id,
      shotFromY: shooter.pos.y,
    };
    return;
  }
}
